// Conteo físico — session workspace (/inventory/conteo/:id).
//
// Three panels: Capturar (manual entry + CSV import), Variación (review:
// KPIs, filters, PDF export), and Postear — applies the counted deltas back
// to on-hand stock via paceWriteServ's post-count verb (countpost.p).
import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem, Tab, TabGroup, TabList,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import PriceCsvDropzone from '../components/PriceCsvDropzone';
import TableSkeleton from '../components/TableSkeleton';
import { getLines, getSession, importCsv, patchLine, postSession, WritesDisabledError } from '../api/inventoryCount';
import { countStatusColor, countStatusLabel, formatCountScope, postLineStatusColor, postLineStatusLabel } from '../lib/format';
import { exportReportPdf } from '../lib/pdf';
import { canPostCount, canUseCount } from '../lib/roles';
import { t } from '../lib/i18n';
import type {
  CountCsvReport, CountLineView, CountSession, CountSummary, CsvRowErr, PostCountResult, User,
} from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

function fmtVariance(v: number): string {
  return v > 0 ? `+${v}` : String(v);
}

export default function InventoryCountSession() {
  const user = useOutletContext<User>();
  if (!canUseCount(user)) return <AccessDenied user={user} />;
  return <InventoryCountSessionInner user={user} />;
}

type TabKey = 'capture' | 'variance' | 'post';
const TABS: TabKey[] = ['capture', 'variance', 'post'];

function InventoryCountSessionInner({ user }: { user: User }) {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);

  const [session, setSession] = useState<CountSession | null>(null);
  const [lines, setLines] = useState<CountLineView[]>([]);
  const [summary, setSummary] = useState<CountSummary | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('capture');
  const [importing, setImporting] = useState(false);
  const [csvReport, setCsvReport] = useState<CountCsvReport | null>(null);

  const load = () => {
    if (!Number.isFinite(sessionId)) { setLoadErr(t.conteo.errors.generic); return; }
    setLoadErr(null);
    setSession(null);
    setSummary(null);
    Promise.all([getSession(sessionId), getLines(sessionId)])
      .then(([s, l]) => { setSession(s); setLines(l.lines); setSummary(l.summary); })
      .catch((e: Error) => setLoadErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sessionId]);

  function refreshLines() {
    return getLines(sessionId).then((l) => { setLines(l.lines); setSummary(l.summary); });
  }

  async function handleSaveLine(lineId: number, counted: number | null) {
    setActionErr(null);
    try {
      await patchLine(sessionId, lineId, counted);
      await refreshLines();
    } catch (e) {
      setActionErr((e as Error).message ?? t.conteo.errors.generic);
    }
  }

  async function handleImportCsv(file: File) {
    setImporting(true);
    setCsvReport(null);
    setActionErr(null);
    try {
      const report = await importCsv(sessionId, file);
      setCsvReport(report);
      await refreshLines();
    } catch (e) {
      setActionErr((e as Error).message ?? t.conteo.errors.generic);
    } finally {
      setImporting(false);
    }
  }

  const showValue = summary != null && summary.gross_value !== null;
  const progressTotal = summary ? summary.counted + summary.uncounted : 0;
  const progressText = summary
    ? t.conteo.progress.replace('{done}', String(summary.counted)).replace('{total}', String(progressTotal))
    : '';

  if (loadErr) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorRetry message={loadErr} onRetry={load} />
      </div>
    );
  }
  if (session === null || summary === null) {
    return (
      <div className="space-y-4">
        <BackLink />
        <TableSkeleton rows={8} cols={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Title>{session.name}</Title>
            <Badge color={countStatusColor(session.status)}>{countStatusLabel(session.status)}</Badge>
          </div>
          <Text className="text-tremor-content text-sm">
            Tienda #{session.sid} · {formatCountScope(session)}
          </Text>
        </div>
        <div className="text-right">
          <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{progressText}</div>
        </div>
      </div>

      {actionErr && (
        <Text className="text-red-600 dark:text-red-400 text-sm">{actionErr}</Text>
      )}

      <TabGroup index={TABS.indexOf(tab)} onIndexChange={(i) => setTab(TABS[i])}>
        <TabList>
          <Tab>{t.conteo.tabs.capture}</Tab>
          <Tab>{t.conteo.tabs.variance}</Tab>
          <Tab>{t.conteo.tabs.post}</Tab>
        </TabList>
      </TabGroup>

      {tab === 'capture' && (
        <CapturePanel
          session={session}
          lines={lines}
          onSaveLine={handleSaveLine}
          onImportCsv={handleImportCsv}
          importing={importing}
          csvReport={csvReport}
        />
      )}
      {tab === 'variance' && (
        <VariancePanel
          session={session}
          lines={lines}
          summary={summary}
          showValue={showValue}
          printedBy={user.username}
        />
      )}
      {tab === 'post' && (
        <PostPanel
          user={user}
          session={session}
          lines={lines}
          onSessionUpdate={setSession}
          refreshLines={refreshLines}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/inventory/conteo" className="text-sm text-tremor-brand hover:underline">
      {t.conteo.back}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Capturar
// ---------------------------------------------------------------------------

interface CapturePanelProps {
  session: CountSession;
  lines: CountLineView[];
  onSaveLine: (lineId: number, counted: number | null) => void;
  onImportCsv: (file: File) => void;
  importing: boolean;
  csvReport: CountCsvReport | null;
}

function CapturePanel({ session, lines, onSaveLine, onImportCsv, importing, csvReport }: CapturePanelProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return lines;
    const q = search.trim().toUpperCase();
    return lines.filter((l) =>
      l.part.toUpperCase().includes(q) || l.line.toUpperCase().includes(q) || l.bin.toUpperCase().includes(q),
    );
  }, [lines, search]);

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <Title className="text-base">{t.conteo.csvImport.title}</Title>
        <Text className="text-xs text-tremor-content">{t.conteo.csvImport.hint}</Text>
        <PriceCsvDropzone onFile={onImportCsv} />
        {importing && <Text className="text-sm text-tremor-content">{t.common.loading}</Text>}
        {csvReport && <CsvImportReport report={csvReport} />}
      </Card>

      <Card className="flex flex-wrap items-end gap-3">
        <label className="text-sm w-full max-w-xs">
          <div className="text-tremor-content text-xs mb-1">{t.conteo.capture.searchLabel}</div>
          <TextInput value={search} onValueChange={setSearch} placeholder={t.conteo.capture.searchPlaceholder} />
        </label>
        <Text className="text-sm text-tremor-content ml-auto">
          {filtered.length} / {lines.length}
        </Text>
      </Card>

      {lines.length === 0 ? (
        <EmptyState message={t.conteo.capture.empty} />
      ) : filtered.length === 0 ? (
        <EmptyState message={t.conteo.capture.noMatches} />
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.conteo.capture.colPart}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.capture.colDesc}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.capture.colBin}</TableHeaderCell>
                  {!session.blind && (
                    <TableHeaderCell className="text-right">{t.conteo.capture.colExpected}</TableHeaderCell>
                  )}
                  <TableHeaderCell className="text-right">{t.conteo.capture.colCounted}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((l) => (
                  <CountLineRow key={l.id} line={l} blind={session.blind} onSave={onSaveLine} />
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

interface CountLineRowProps {
  line: CountLineView;
  blind: boolean;
  onSave: (lineId: number, counted: number | null) => void;
}

function CountLineRow({ line, blind, onSave }: CountLineRowProps) {
  const [value, setValue] = useState(line.counted == null ? '' : String(line.counted));
  useEffect(() => {
    setValue(line.counted == null ? '' : String(line.counted));
  }, [line.counted]);

  function commit() {
    const trimmed = value.trim();
    if (trimmed === '') {
      if (line.counted !== null) onSave(line.id, null);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      setValue(line.counted == null ? '' : String(line.counted));
      return;
    }
    if (n !== line.counted) onSave(line.id, n);
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{line.line} {line.part}</TableCell>
      <TableCell>{line.pdesc}</TableCell>
      <TableCell>{line.bin || '—'}</TableCell>
      {!blind && <TableCell className="text-right tabular-nums">{line.expected}</TableCell>}
      <TableCell className="text-right">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
          className="w-24 rounded border border-tremor-border px-2 py-1 text-sm text-right tabular-nums bg-tremor-background"
          aria-label={`${t.conteo.capture.colCounted} ${line.line} ${line.part}`}
        />
      </TableCell>
    </TableRow>
  );
}

function CsvImportReport({ report }: { report: CountCsvReport }) {
  return (
    <div className="space-y-3">
      <Text className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
        {t.conteo.csvImport.applied.replace('{n}', String(report.applied.length))}
      </Text>
      <CsvErrTable title={t.conteo.csvImport.unmatched} rows={report.unmatched} color="amber" />
      <CsvErrTable title={t.conteo.csvImport.duplicate} rows={report.duplicate} color="amber" />
      <CsvErrTable title={t.conteo.csvImport.nonNumeric} rows={report.non_numeric} color="red" />
    </div>
  );
}

function CsvErrTable({ title, rows, color }: { title: string; rows: CsvRowErr[]; color: 'amber' | 'red' }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Text className="font-medium">{title}</Text>
        <Badge color={color}>{rows.length}</Badge>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.conteo.csvImport.cols.row}</TableHeaderCell>
            <TableHeaderCell>{t.conteo.csvImport.cols.part}</TableHeaderCell>
            <TableHeaderCell>{t.conteo.csvImport.cols.reason}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.line_num}</TableCell>
              <TableCell>{r.line} {r.part}</TableCell>
              <TableCell>{r.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variación
// ---------------------------------------------------------------------------

type VarianceFilter = 'all' | 'short' | 'over' | 'uncounted' | 'variance';
const VARIANCE_FILTERS: VarianceFilter[] = ['all', 'short', 'over', 'uncounted', 'variance'];

interface VariancePanelProps {
  session: CountSession;
  lines: CountLineView[];
  summary: CountSummary;
  showValue: boolean;
  printedBy: string;
}

function VariancePanel({ session, lines, summary, showValue, printedBy }: VariancePanelProps) {
  const [filter, setFilter] = useState<VarianceFilter>('all');

  const rows = useMemo(() => {
    let pool = lines;
    switch (filter) {
      case 'short':     pool = pool.filter((l) => l.counted != null && l.variance < 0); break;
      case 'over':      pool = pool.filter((l) => l.counted != null && l.variance > 0); break;
      case 'uncounted': pool = pool.filter((l) => l.counted == null); break;
      case 'variance':  pool = pool.filter((l) => l.counted != null && l.variance !== 0); break;
      default: break;
    }
    return [...pool].sort((a, b) => {
      const av = showValue ? Math.abs(a.value ?? 0) : Math.abs(a.variance);
      const bv = showValue ? Math.abs(b.value ?? 0) : Math.abs(b.variance);
      return bv - av;
    });
  }, [lines, filter, showValue]);

  function handleExportPdf() {
    const columns: string[] = [t.conteo.variance.cols.part, t.conteo.variance.cols.desc, t.conteo.variance.cols.bin];
    columns.push(t.conteo.variance.cols.expected);
    columns.push(t.conteo.variance.cols.counted, t.conteo.variance.cols.variance);
    if (showValue) columns.push(t.conteo.variance.cols.value);
    columns.push(t.conteo.variance.cols.drift);

    // Everything after part/desc/bin (the first 3 columns) is numeric/right-aligned.
    const numericColumns = columns.map((_, i) => i).slice(3);

    const pdfRows: Array<Array<string | number>> = rows.map((l) => {
      const row: Array<string | number> = [`${l.line} ${l.part}`, l.pdesc, l.bin || '—'];
      row.push(l.expected);
      row.push(l.counted ?? '—', l.counted == null ? '—' : fmtVariance(l.variance));
      if (showValue) row.push(l.value != null ? fmtMxn.format(l.value) : '—');
      row.push(l.drift ?? '—');
      return row;
    });

    exportReportPdf({
      title: t.conteo.variance.printTitle,
      meta: [`${session.name} · Tienda #${session.sid} · ${formatCountScope(session)}`],
      printedBy,
      columns,
      rows: pdfRows,
      numericColumns,
      filename: `conteo_${session.id}_variacion.pdf`,
    });
  }

  return (
    <div className="space-y-4">
      <Card className={`grid grid-cols-2 md:grid-cols-3 ${showValue ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-4`}>
        <Kpi label={t.conteo.variance.kpi.counted} value={String(summary.counted)} />
        <Kpi label={t.conteo.variance.kpi.uncounted} value={String(summary.uncounted)} />
        <Kpi label={t.conteo.variance.kpi.withVariance} value={String(summary.with_variance)} />
        <Kpi label={t.conteo.variance.kpi.netUnits} value={String(summary.net_units)} />
        {showValue && <Kpi label={t.conteo.variance.kpi.grossValue} value={fmtMxn.format(summary.gross_value ?? 0)} />}
        {showValue && <Kpi label={t.conteo.variance.kpi.netValue} value={fmtMxn.format(summary.net_value ?? 0)} />}
      </Card>

      <Card className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1 flex-wrap">
          {VARIANCE_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                'px-2 py-1 rounded border text-xs ' +
                (filter === f
                  ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                  : 'border-tremor-border text-tremor-content hover:text-tremor-content-strong')
              }
            >
              {t.conteo.variance.filters[f]}
            </button>
          ))}
        </div>
        <Button size="xs" variant="secondary" className="ml-auto" onClick={handleExportPdf} disabled={rows.length === 0}>
          {t.conteo.variance.exportPdf}
        </Button>
      </Card>

      {rows.length === 0 ? (
        <EmptyState message={t.conteo.variance.empty} />
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.conteo.variance.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.variance.cols.desc}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.variance.cols.bin}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.conteo.variance.cols.expected}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.conteo.variance.cols.counted}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.conteo.variance.cols.variance}</TableHeaderCell>
                  {showValue && <TableHeaderCell className="text-right">{t.conteo.variance.cols.value}</TableHeaderCell>}
                  <TableHeaderCell className="text-right">{t.conteo.variance.cols.drift}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.line} {l.part}</TableCell>
                    <TableCell>{l.pdesc}</TableCell>
                    <TableCell>{l.bin || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.expected}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.counted ?? '—'}</TableCell>
                    <TableCell
                      className={
                        'text-right tabular-nums font-medium ' +
                        (l.counted == null
                          ? 'text-tremor-content'
                          : l.variance > 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : l.variance < 0
                              ? 'text-red-600 dark:text-red-400'
                              : '')
                      }
                    >
                      {l.counted == null ? '—' : fmtVariance(l.variance)}
                    </TableCell>
                    {showValue && (
                      <TableCell className="text-right tabular-nums">
                        {l.value != null ? fmtMxn.format(l.value) : '—'}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums text-tremor-content">{l.drift ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Postear
// ---------------------------------------------------------------------------

interface PostPanelProps {
  user: User;
  session: CountSession;
  lines: CountLineView[];
  onSessionUpdate: (session: CountSession) => void;
  refreshLines: () => Promise<void>;
}

function PostPanel({ user, session, lines, onSessionUpdate, refreshLines }: PostPanelProps) {
  const canPost = canPostCount(user);
  const writesEnabled = !!user.features?.writes_enabled;

  const [mode, setMode] = useState(session.post_mode || 'delta');
  const [uncounted, setUncounted] = useState(session.uncounted || 'skip');
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState<string | null>(null);
  const [results, setResults] = useState<PostCountResult[] | null>(null);

  if (!canPost) {
    return (
      <Card>
        <Title className="text-base">{t.conteo.post.title}</Title>
        <Text className="text-tremor-content text-sm mt-2">{t.conteo.post.noPermission}</Text>
      </Card>
    );
  }

  if (!writesEnabled) {
    return (
      <Card>
        <Text className="text-amber-600 dark:text-amber-400">{t.conteo.post.writesOff}</Text>
      </Card>
    );
  }

  const posteado = session.status === 'posteado';
  // Once any line has been attempted, the button relabels to "restantes" —
  // derived from `lines` (not local-only state) so it reads correctly even
  // after a reload mid-way through a partially-posted session.
  const alreadyAttempted = lines.some((l) => l.post_status !== '');

  async function handlePost() {
    setPosting(true);
    setPostErr(null);
    try {
      const res = await postSession(session.id, { mode, uncounted });
      setResults(res.results);
      onSessionUpdate(res.session);
      await refreshLines();
    } catch (e) {
      setPostErr(e instanceof WritesDisabledError ? t.conteo.post.writesOff : (e as Error).message ?? t.conteo.errors.generic);
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <Title className="text-base">{t.conteo.post.title}</Title>

        {posteado ? (
          <div>
            <Text className="text-emerald-600 dark:text-emerald-400 font-medium">{t.conteo.post.posteado}</Text>
            {session.posted_by && (
              <Text className="text-tremor-content text-xs mt-1">
                {session.posted_by} · {session.posted_at ? new Date(session.posted_at).toLocaleString('es-MX') : ''}
              </Text>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm w-44">
                <div className="text-tremor-content text-xs mb-1">{t.conteo.post.mode}</div>
                <Select value={mode} onValueChange={setMode} enableClear={false}>
                  <SelectItem value="delta">{t.conteo.form.postModeOptions.delta}</SelectItem>
                  <SelectItem value="overwrite">{t.conteo.form.postModeOptions.overwrite}</SelectItem>
                </Select>
              </label>
              <label className="text-sm w-48">
                <div className="text-tremor-content text-xs mb-1">{t.conteo.post.uncounted}</div>
                <Select value={uncounted} onValueChange={setUncounted} enableClear={false}>
                  <SelectItem value="skip">{t.conteo.form.uncountedOptions.skip}</SelectItem>
                  <SelectItem value="zero">{t.conteo.form.uncountedOptions.zero}</SelectItem>
                </Select>
              </label>
              <Button onClick={handlePost} loading={posting} disabled={posting}>
                {alreadyAttempted ? t.conteo.post.restantes : t.conteo.post.button}
              </Button>
            </div>
            {postErr && <Text className="text-red-600 dark:text-red-400 text-sm">{postErr}</Text>}
          </>
        )}
      </Card>

      {results !== null && results.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.conteo.post.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.post.cols.status}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.conteo.post.cols.change}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={`${r.line}-${r.part}-${i}`}>
                    <TableCell className="font-medium">{r.line} {r.part}</TableCell>
                    <TableCell>
                      <Badge color={postLineStatusColor(r.status)}>{postLineStatusLabel(r.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.old} → {r.new}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
